/**
 * অবস্থা ৭ · সিঙ্ক ত্রুটি — `P0 States § ৭` (Failure · Sync). `E_SYNC`.
 *
 * THE DESIGN DECISION THIS STATE EXISTS TO CARRY:
 *
 * "সিঙ্ক ত্রুটিতে একটি ব্যানারের বদলে প্রতিটি সারিতে সময় লিখতে হয়েছে; ব্যানার একবার পড়ে
 * মানুষ ভুলে যান, সারির লেবেল ভোলা যায় না।" — every row is labelled with its OWN time.
 * The frame note repeats it: "Stale rows are labelled with the timestamp they came
 * from, one per row, rather than one banner the user scrolls past. Never blank a list
 * that has usable cached data in it."
 *
 * The frame draws a banner too, but the banner is not the mechanism — the per-row
 * label is. Every row in `staleRows` carries its own `stale_label_bn`; a row without
 * one cannot be marked stale by this component.
 *
 * THE LABEL IS NOT COMPOSED HERE, AND CANNOT BE.
 *
 * The drawn label is `১১:৪০-এর তথ্য`: Bangla text around Bangla numerals. docs/05
 * § conventions — "the client must never compose Bangla from fragments" — so it cannot
 * be built from `ActivityRow.stale_at`, which is an RFC 3339 timestamp. WP-D found the
 * same wall and added `stale_label_bn` to its row type for the API to send; this
 * component consumes that field and formats nothing.
 *
 * W1-04 · ROWS ARE REAL OR ABSENT, AND SO IS EVERYTHING ABOUT THEM.
 * With no `staleRows` this frame used to draw the design's two rows — two orders, an
 * electrician's name, amounts — over any `E_SYNC`, which is what every Worker route
 * answers when a port is unbound. Now: no rows given, no rows drawn, and no "what you see
 * may be old" note under a list that is not there. The drawn banner sentence names a time
 * ("শেষ আপডেট ১১:৪০") that none of these failures has; the banner now carries the server's
 * sentence, or the state's own drawn name, beside the retry that really retries. The
 * reference is the error body's, in the footer where § ৫ puts one. The activity chrome —
 * title, tabs, tab bar — is drawn only on the activity surface; on home or on a console
 * it named a screen the person was not on. `docs/deviations/W1-04.md` (S7).
 *
 * `#FDF6E8` / `#B07A16` on the banner: the off-palette warning pair. Flagged.
 */
import { formatTaka } from '@oprappo/contracts';
import { TabBar, type TabBarKey } from '@oprappo/ui';
import { StateFrame, WARNING_PANEL, OutlineChip } from './chrome.js';
import { SYNC_ERROR } from './data.js';
import { STATE_LABEL_BN } from './keys.js';

export interface StaleRow {
  id: string;
  glyph: 'service' | 'market';
  label_bn: string;
  meta_bn: string;
  amount_minor: number;
  /** Sent by the API, already localised. Never built on the client. */
  stale_label_bn: string;
}

export interface SyncErrorStateProps {
  /** Rows from cache. Each carries its own label; there is no list-wide timestamp. */
  staleRows?: readonly StaleRow[] | undefined;
  /** `error.message_bn`. Absent or empty: the banner carries the state's drawn name. */
  messageBn?: string | undefined;
  /** `error.reference`. Absent or empty: not drawn. */
  reference?: string | undefined;
  /** Draw অ্যাক্টিভিটি's title, tabs and tab bar — only where the person IS on activity. */
  activityChrome?: boolean | undefined;
  onRetry?: (() => void) | undefined;
  onTab?: ((key: TabBarKey) => void) | undefined;
}

function said(value: string | undefined): value is string {
  return value !== undefined && value.trim() !== '';
}

function RowGlyph({ glyph }: { glyph: 'service' | 'market' }): JSX.Element {
  return (
    <svg width={19} height={19} viewBox="0 0 24 24" fill="none" aria-hidden>
      {glyph === 'service' ? (
        <path d="M6 3.5v6a6 6 0 0012 0v-6" stroke="#0E3A45" strokeWidth={2} strokeLinecap="round" />
      ) : (
        <path d="M3.5 7h17l-1.5 13h-14z" stroke="#0E3A45" strokeWidth={2} strokeLinejoin="round" />
      )}
    </svg>
  );
}

export function SyncErrorState({
  staleRows,
  messageBn,
  reference,
  activityChrome = true,
  onRetry,
  onTab,
}: SyncErrorStateProps = {}): JSX.Element {
  const d = SYNC_ERROR;
  const rows = staleRows ?? [];
  return (
    <StateFrame stateKey="sync_error">
      {activityChrome ? (
        <div className="bg-white px-[16px] pt-[16px] border-b border-border">
          <div className="font-anek font-semibold text-[22px] leading-[1.25] text-ink mb-[14px]">{d.title_bn}</div>
          <div role="tablist" className="flex">
            {d.tabs.map((t, i) => (
              <div
                key={t.id}
                role="tab"
                aria-selected={i === 0}
                className={
                  'px-[3px] pb-[11px] font-anek text-[15px] leading-[1.75]' +
                  (i < d.tabs.length - 1 ? ' mr-[22px]' : '') +
                  (i === 0 ? ' font-semibold text-teal border-b-2 border-teal mb-[-1px]' : ' font-medium text-muted')
                }
              >
                {t.label_bn}
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <div role="status" className={WARNING_PANEL + ' px-[16px] py-[12px] flex justify-between items-center gap-[11px]'}>
        <div className="flex-1 font-anek font-normal text-[14px] leading-[1.75] text-ink2">
          {said(messageBn) ? messageBn : STATE_LABEL_BN.sync_error}
        </div>
        <button
          type="button"
          onClick={onRetry}
          className="font-anek font-semibold text-[14px] leading-[1.75] text-amber-text flex-none bg-transparent border-0 p-0 rounded-none"
        >
          {d.banner_action_bn}
        </button>
      </div>

      <div className="flex-1 px-[16px] oprappo-scroll">
        {rows.map((r) => (
          <div key={r.id} className="py-[14px] border-b border-border flex gap-[12px] items-center">
            <div className="box-content w-[36px] h-[36px] border border-border bg-white flex items-center justify-center flex-none">
              <RowGlyph glyph={r.glyph} />
            </div>
            <div className="flex-1">
              <div className="font-anek font-medium text-[15px] leading-[1.75] text-ink">{r.label_bn}</div>
              <div className="font-anek font-normal text-[13.5px] leading-[1.75] text-muted mt-[1px]">
                {r.meta_bn}
              </div>
            </div>
            <div className="text-right flex-none">
              <div className="font-manrope font-semibold text-[15px] leading-[1] text-ink tabular-nums">
                {formatTaka(r.amount_minor)}
              </div>
              {/* ONE LABEL PER ROW. This is the state, not the banner above. */}
              <div className="mt-[5px]">
                <OutlineChip labelBn={r.stale_label_bn} padding="row" />
              </div>
            </div>
          </div>
        ))}

        {rows.length === 0 ? null : (
          <div className="bg-white border border-border p-[14px] mt-[18px]">
            <div className="font-anek font-medium text-[15px] leading-[1.75] text-ink">{d.note_title_bn}</div>
            <div className="font-anek font-normal text-[13.5px] leading-[1.75] text-body mt-[4px]">
              {d.note_body_bn}
            </div>
          </div>
        )}
      </div>

      {said(reference) ? (
        <div className="bg-sand border-t border-border px-[16px] py-[12px] flex justify-between items-center gap-[11px]">
          <div className="font-anek font-normal text-[13px] leading-[1.75] text-muted">{d.support_bn}</div>
          <div className="font-manrope font-medium text-[13px] leading-[1.5] text-muted flex-none">{reference}</div>
        </div>
      ) : null}

      {activityChrome ? <TabBar activeKey="activity" onChange={onTab} /> : null}
    </StateFrame>
  );
}
